import React, { useState, useEffect } from 'react';

interface PaymentScreenProps {
  currentCompany: any;
  initialPlan?: string;
  onSuccess: () => void;
  onBack: () => void;
}

const PLANOS = [
  {
    id: 'starter',
    nome: 'Starter',
    preco: { monthly: 149, annual: 1490 },
    precoAnualMensal: 124, // equivalente mensal no plano anual
    desc: 'Até 3 técnicos · 1 admin · 1 assistente',
    color: 'var(--muted)',
    features: ['O.S. de montagem e assistência', 'Agenda de Montagem', 'Calculadora de Markup', 'Exportação CSV'],
  },
  {
    id: 'pro',
    nome: 'Pro',
    preco: { monthly: 299, annual: 2990 },
    precoAnualMensal: 249,
    desc: 'Até 10 técnicos · 3 admins · 3 assistentes',
    color: 'var(--blue)',
    recommended: true,
    features: ['Tudo do Starter', 'Painel de Faturamento', 'Gastos com Materiais', 'Histórico de Orçamentos', 'Produtividade e Ranking'],
  },
  {
    id: 'business',
    nome: 'Business',
    preco: { monthly: 399, annual: 3990 },
    precoAnualMensal: 332,
    desc: 'Ilimitado · Todos os recursos',
    color: 'var(--purple)',
    features: ['Tudo do Pro', 'Técnicos ilimitados', 'Relatórios entre períodos', 'Audit log', 'Suporte prioritário'],
  },
];

export const PaymentScreen: React.FC<PaymentScreenProps> = ({ currentCompany, initialPlan, onSuccess, onBack }) => {
  const [selectedPlano, setSelectedPlano] = useState(initialPlan || 'pro');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setLoading(false);
      }
    };

    const handleFocus = () => {
      setLoading(false);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const plano = PLANOS.find(p => p.id === selectedPlano)!;

  const CHECKOUT_URLS: Record<string, string> = {
    'starter-monthly': 'https://www.mercadopago.com.br/subscriptions/checkout?preapproval_plan_id=23a536e7415748ab91b2a215294ddd60',
    'pro-monthly': 'https://www.mercadopago.com.br/subscriptions/checkout?preapproval_plan_id=a8ea118805d94238926506711e05201b',
    'business-monthly': 'https://www.mercadopago.com.br/subscriptions/checkout?preapproval_plan_id=bf130892d9234ac484bf9cff1f5ecf5b',
    'starter-annual': 'https://www.mercadopago.com.br/subscriptions/checkout?preapproval_plan_id=68a89ec856f246d587bb135a13831434',
    'pro-annual': 'https://www.mercadopago.com.br/subscriptions/checkout?preapproval_plan_id=cb344770f2204c1a888dc255781d8856',
    'business-annual': 'https://www.mercadopago.com.br/subscriptions/checkout?preapproval_plan_id=b8597d7a69f7432c9ee95339d468ee70',
  };

  const handleCreateSubscription = () => {
    setLoading(true);
    const planKey = `${selectedPlano}-${billingCycle}`;
    const checkoutUrl = CHECKOUT_URLS[planKey];
    if (!checkoutUrl) {
      alert('Plano não encontrado.');
      setLoading(false);
      return;
    }
    window.location.href = checkoutUrl;
  };

  const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <div style={{ width:'100%', maxWidth: 720 }}>

        <div style={{ textAlign:'center', marginBottom:28 }}>
          <h2 style={{ fontSize:22, fontWeight:700, color:'var(--text)', marginBottom:8 }}>
            Escolha seu plano
          </h2>
          <p style={{ fontSize:14, color:'var(--muted)' }}>
            Escolha o plano ideal para a sua equipe
          </p>
        </div>

        {/* Seletor de Ciclo */}
        <div style={{ display:'flex', gap:2, background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:7, padding:3, width:'fit-content', margin:'0 auto 20px' }}>
          <button
            type="button"
            onClick={() => setBillingCycle('monthly')}
            style={{ padding:'6px 20px', borderRadius:5, fontSize:13, fontWeight:500, cursor:'pointer', border: billingCycle==='monthly' ? '1px solid var(--border)' : 'none', background: billingCycle==='monthly' ? 'var(--bg2)' : 'transparent', color: billingCycle==='monthly' ? 'var(--text)' : 'var(--muted)', transition:'all .15s' }}
          >
            Mensal
          </button>
          <button
            type="button"
            onClick={() => setBillingCycle('annual')}
            style={{ padding:'6px 20px', borderRadius:5, fontSize:13, fontWeight:500, cursor:'pointer', border: billingCycle==='annual' ? '1px solid var(--border)' : 'none', background: billingCycle==='annual' ? 'var(--bg2)' : 'transparent', color: billingCycle==='annual' ? 'var(--text)' : 'var(--muted)', display:'flex', alignItems:'center', gap:6, transition:'all .15s' }}
          >
            Anual
            <span style={{ fontSize:10, background:'rgba(16,185,129,.15)', border:'1px solid rgba(16,185,129,.3)', color:'#34d399', padding:'1px 6px', borderRadius:20, fontWeight:700 }}>
              2 meses grátis
            </span>
          </button>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:20 }}>
          {PLANOS.map(p => (
            <div
              key={p.id}
              onClick={() => setSelectedPlano(p.id)}
              style={{
                background:'var(--bg2)',
                border:`2px solid ${selectedPlano === p.id ? p.color : 'var(--border)'}`,
                borderRadius:12, padding:'20px 16px', cursor:'pointer',
                transition:'border-color .15s', position:'relative',
              }}
            >
              {p.recommended && (
                <div style={{ position:'absolute', top:-10, left:'50%', transform:'translateX(-50%)', background:'var(--blue)', color:'#fff', fontSize:10, fontWeight:700, padding:'2px 10px', borderRadius:20, whiteSpace:'nowrap' }}>
                  RECOMENDADO
                </div>
              )}
              <div style={{ fontSize:15, fontWeight:700, color: p.color, marginBottom:4 }}>{p.nome}</div>
              <div style={{ fontFamily:'DM Mono,monospace', fontSize:20, fontWeight:700, color:'var(--text)', marginBottom:4 }}>
                {billingCycle === 'annual' ? (
                  <>
                    {fmt(p.preco.annual)}<span style={{ fontSize:12, fontWeight:400, color:'var(--muted)' }}>/ano</span>
                    <div style={{ fontSize:11, fontWeight:600, color:'var(--sf-green)', marginTop:4 }}>
                      equivale a {fmt(p.precoAnualMensal)}/mês
                    </div>
                    <div style={{ fontSize:10, color:'var(--muted)', fontWeight:400, marginTop:2 }}>
                      2 meses grátis inclusos
                    </div>
                  </>
                ) : (
                  <>
                    {fmt(p.preco.monthly)}<span style={{ fontSize:12, fontWeight:400, color:'var(--muted)' }}>/mês</span>
                  </>
                )}
              </div>
              <div style={{ fontSize:11, color:'var(--muted)', marginTop:8, marginBottom:12 }}>{p.desc}</div>
              <ul style={{ listStyle:'none', display:'flex', flexDirection:'column', gap:5 }}>
                {p.features.map(f => (
                  <li key={f} style={{ display:'flex', alignItems:'center', gap:6, fontSize:11, color:'var(--muted)' }}>
                    <svg width="11" height="11" fill="none" stroke="var(--green)" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:10, marginTop:20 }}>
          <button
            type="button"
            onClick={handleCreateSubscription}
            disabled={loading}
            style={{ width:'100%', padding:'13px', borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer', border:'none', background:'var(--blue)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
            {loading ? 'Aguarde...' : `Assinar com recorrência · ${fmt(billingCycle === 'annual' ? plano.preco.annual : plano.preco.monthly)}${billingCycle === 'annual' ? '/ano' : '/mês'}`}
          </button>
        </div>

        <div style={{ display:'flex', justifyContent:'center', marginTop:20 }}>
          <button
            type="button"
            onClick={onBack}
            style={{ padding:'10px 24px', borderRadius:8, fontSize:13, fontWeight:500, cursor:'pointer', background:'transparent', border:'1px solid var(--border)', color:'var(--muted)' }}
          >
            Voltar
          </button>
        </div>

      </div>
    </div>
  );
};
